import React, { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Card, Btn, Spinner, ErrBox, Input, Field } from '../../../components/UI';
import { procurementAPI, contractAPI } from '../../../utils/api';
import { fmtRp, fmtDate } from '../../../utils/format';

const THRESHOLD_200JT = 200000000;

export default function DetailTab({ id, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [sirupNo, setSirupNo] = useState('');
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState('');

  useEffect(() => { if (id) load(); }, [id]);

  const load = async () => {
    setLoading(true); setError('');
    try { const r = await procurementAPI.getById(id); setData(r.data); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const loadContracts = async () => {
    try { const r = await contractAPI.getAll('?limit=100'); setContracts(r.data || []); } catch {}
  };

  const handleAction = async (action, notes) => {
    setActioning(true);
    try {
      await procurementAPI.action(id, { action, notes });
      alert(`PR berhasil di-${action === 'APPROVE' ? 'setujui → MENUNGGU_SIRUP' : 'tolak'}`);
      load();
    } catch (e) { alert('Gagal: ' + e.message); }
    finally { setActioning(false); }
  };

  const handleSirupCheck = async () => {
    if (!window.confirm('Verifikasi tayang SIRUP? Status akan berubah ke PROSES_PENGADAAN.')) return;
    setActioning(true);
    try {
      await procurementAPI.sirupCheck(id, { sirup_no: sirupNo });
      alert('SIRUP verified → PROSES_PENGADAAN');
      load();
    } catch (e) { alert('Gagal: ' + e.message); }
    finally { setActioning(false); }
  };

  const handleApprovePengadaan = async () => {
    if (!window.confirm('Approve pengadaan? Akan lanjut ke proses payment.')) return;
    setActioning(true);
    try {
      await procurementAPI.approvePengadaan(id);
      alert('Pengadaan approved');
      load();
    } catch (e) { alert('Gagal: ' + e.message); }
    finally { setActioning(false); }
  };

  const handleLinkContract = async () => {
    if (!selectedContract) return alert('Pilih kontrak terlebih dahulu');
    setActioning(true);
    try {
      await procurementAPI.linkContract(id, { contract_id: selectedContract });
      alert('Kontrak berhasil dihubungkan');
      load();
    } catch (e) { alert('Gagal: ' + e.message); }
    finally { setActioning(false); }
  };

  const handleSubmitPayment = async () => {
    if (!window.confirm('Kirim ke Payment Request? Pagu akan dipotong.')) return;
    setActioning(true);
    try {
      await procurementAPI.submitPayment(id);
      alert('Berhasil dibuatkan Payment Request');
      load();
    } catch (e) { alert('Gagal: ' + e.message); }
    finally { setActioning(false); }
  };

  const handleReceive = async () => {
    if (!window.confirm('Terima barang? Akan membuat asset/inventory sesuai tipe.')) return;
    setActioning(true);
    try {
      await procurementAPI.receive(id);
      alert('Barang berhasil diterima');
      load();
    } catch (e) { alert('Gagal: ' + e.message); }
    finally { setActioning(false); }
  };

  const handleComplete = async () => {
    if (!window.confirm('Tandai selesai?')) return;
    setActioning(true);
    try {
      await procurementAPI.complete(id);
      alert('PR selesai');
      load();
    } catch (e) { alert('Gagal: ' + e.message); }
    finally { setActioning(false); }
  };

  const getStatusBadge = (status) => {
    const cfg = {
      DRAFT: { bg: '#F3F4F6', color: '#374151' }, PENDING_APPROVAL: { bg: '#FFFBEB', color: '#D97706' },
      MENUNGGU_SIRUP: { bg: '#EDE9FE', color: '#5B21B6' }, PROSES_PENGADAAN: { bg: '#DBEAFE', color: '#1E40AF' },
      APPROVED: { bg: '#ECFDF5', color: '#059669' }, REJECTED: { bg: '#FEF2F2', color: '#DC2626' },
      ORDERED: { bg: '#DBEAFE', color: '#1E40AF' }, RECEIVED: { bg: '#E9D5FF', color: '#581C87' },
      COMPLETED: { bg: '#ECFDF5', color: '#059669' },
    };
    const c = cfg[status] || { bg: '#F3F4F6', color: '#374151' };
    return <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: c.bg, color: c.color }}>{status}</span>;
  };

  const getItemLabel = (d) => {
    if (d.item_type === 'ASSET' || d.asset_category_id) return `${d.category_name || ''} - ${d.asset_name || d.description || '-'}`;
    return `${d.item_code || ''} ${d.item_name || d.item_id || '-'}`.trim();
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>;
  if (error) return <ErrBox msg={error} onRetry={load} />;
  if (!data) return null;

  const s = data;

  return (
    <div>
      <Btn variant="ghost" onClick={onBack} style={{ marginBottom: 16 }}>
        <ArrowLeft size={14} style={{ marginRight: 6 }} /> Kembali
      </Btn>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>{s.request_no}</h2>
            <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 13 }}>
              Tanggal: {fmtDate(s.request_date)} · Tipe: <strong>{s.request_type}</strong>
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {getStatusBadge(s.status)}
            {s.metode_pengadaan && (
              <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                background: s.metode_pengadaan === 'TENDER' ? '#FEF2F2' : '#ECFDF5',
                color: s.metode_pengadaan === 'TENDER' ? '#DC2626' : '#059669' }}>
                {s.metode_pengadaan === 'TENDER' ? 'TENDER (≥200jt)' : 'PENGADAAN LANGSUNG (<200jt)'}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20, padding: 16, background: '#F8FAFC', borderRadius: 10 }}>
          <div><strong style={{ fontSize: 11, color: '#6B7280', display: 'block' }}>Unit</strong><span style={{ fontSize: 13 }}>{s.unit_name || '-'}</span></div>
          <div><strong style={{ fontSize: 11, color: '#6B7280', display: 'block' }}>Vendor</strong><span style={{ fontSize: 13 }}>{s.vendor_name || '-'}</span></div>
          <div><strong style={{ fontSize: 11, color: '#6B7280', display: 'block' }}>Akun</strong><span style={{ fontSize: 13 }}>{s.coa_kode || '-'} {s.coa_nama ? '/ ' + s.coa_nama : ''}</span></div>
          <div><strong style={{ fontSize: 11, color: '#6B7280', display: 'block' }}>Total</strong><span style={{ fontSize: 18, fontWeight: 700, color: Number(s.total_amount) >= THRESHOLD_200JT ? '#DC2626' : '#2563EB', fontFamily: 'monospace' }}>{fmtRp(s.total_amount)}</span></div>
        </div>

        {s.budget_pagu > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20, padding: 12, background: '#FEF9EC', borderRadius: 8, border: '1px solid #F59E0B30' }}>
            <div><strong style={{ fontSize: 11, color: '#6B7280' }}>Pagu</strong><p style={{ margin: 2, fontFamily: 'monospace', fontWeight: 600 }}>{fmtRp(s.budget_pagu)}</p></div>
            <div><strong style={{ fontSize: 11, color: '#6B7280' }}>Terpakai</strong><p style={{ margin: 2, fontFamily: 'monospace' }}>{fmtRp(s.budget_realisasi || 0)}</p></div>
            <div><strong style={{ fontSize: 11, color: '#6B7280' }}>Sisa</strong><p style={{ margin: 2, fontFamily: 'monospace', fontWeight: 600, color: s.budget_sisa < 0 ? '#DC2626' : '#059669' }}>{fmtRp(s.budget_sisa)}</p></div>
          </div>
        )}

        {s.notes && <div style={{ marginBottom: 16, padding: 12, background: '#F9FAFB', borderRadius: 8 }}><strong style={{ fontSize: 12 }}>Catatan:</strong><p style={{ margin: '4px 0 0', fontSize: 13 }}>{s.notes}</p></div>}

        <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#111827' }}>
          Detail Item — {s.request_type === 'ASSET' ? 'Asset Tetap' : 'Barang Persediaan'}
        </h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#F8FAFC' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Nama / Barang</th>
            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Lokasi</th>
            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Qty</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Harga</th>
            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Total</th>
          </tr></thead>
          <tbody>
            {(s.details || []).map((d, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td style={{ padding: '8px 12px' }}>
                  {s.request_type === 'ASSET' ? (
                    <div>
                      <span style={{ fontWeight: 600, color: '#581C87' }}>{d.category_name || '-'}</span>
                      <br/><span style={{ color: '#374151' }}>{d.asset_name || d.description || '-'}</span>
                    </div>
                  ) : (
                    <div>
                      <span style={{ fontWeight: 600, color: '#1E40AF' }}>{d.item_code || '-'}</span>
                      <br/><span style={{ color: '#374151' }}>{d.item_name || d.item_id || '-'}</span>
                    </div>
                  )}
                </td>
                <td style={{ padding: '8px 12px', color: '#6B7280' }}>{d.warehouse_name || '-'}</td>
                <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.quantity}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtRp(d.unit_price)}</td>
                <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtRp(d.total_price)}</td>
              </tr>
            ))}
            {(s.details || []).length === 0 && (
              <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#9CA3AF' }}>Tidak ada item</td></tr>
            )}
          </tbody>
        </table>

        {s.status === 'MENUNGGU_SIRUP' && (
          <div style={{ marginTop: 20, padding: 16, background: '#EDE9FE', borderRadius: 10, border: '1px solid #C4B5FD' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#5B21B6' }}>Checklist SIRUP (PROGAR)</h4>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6B7280' }}>Verifikasi bahwa pengadaan ini sudah tayang di SIRUP sebelum melanjutkan.</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <Field label="No. SIRUP (opsional)" style={{ flex: 1, margin: 0 }}>
                <Input value={sirupNo} onChange={e => setSirupNo(e.target.value)} placeholder="Masukkan nomor SIRUP..." />
              </Field>
              <Btn disabled={actioning} onClick={handleSirupCheck}>{actioning ? 'Memproses...' : 'Verifikasi SIRUP'}</Btn>
            </div>
          </div>
        )}

        {s.status === 'PROSES_PENGADAAN' && s.metode_pengadaan === 'TENDER' && !s.contract_id && (
          <div style={{ marginTop: 20, padding: 16, background: '#FEF2F2', borderRadius: 10, border: '1px solid #FECACA' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#DC2626' }}>Validasi Kontrak PPK (Wajib untuk Tender)</h4>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6B7280' }}>Pengadaan ≥200jt wajib dihubungkan dengan kontrak vendor sebelum approve.</p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <Field label="Kontrak Vendor" style={{ flex: 1, margin: 0 }}>
                <select value={selectedContract} onChange={e => setSelectedContract(e.target.value)} onClick={() => { if (!contracts.length) loadContracts(); }}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #D1D5DB', fontSize: 13 }}>
                  <option value="">-- Pilih Kontrak --</option>
                  {contracts.map(c => <option key={c.id} value={c.id}>{c.contract_no} - {c.judul} ({fmtRp(c.nilai_kontrak)})</option>)}
                </select>
              </Field>
              <Btn disabled={actioning} onClick={handleLinkContract}>{actioning ? 'Memproses...' : 'Hubungkan Kontrak'}</Btn>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24, paddingTop: 16, borderTop: '1px solid #E5E7EB' }}>
          {s.status === 'DRAFT' && (
            <>
              <Btn variant="danger" disabled={actioning} onClick={() => handleAction('REJECT', '')}>Tolak</Btn>
              <Btn disabled={actioning} onClick={() => handleAction('APPROVE', '')}>Setujui → SIRUP</Btn>
            </>
          )}
          {s.status === 'PROSES_PENGADAAN' && (
            <Btn disabled={actioning} onClick={handleApprovePengadaan}>
              {actioning ? 'Memproses...' : s.metode_pengadaan === 'TENDER' ? 'Approve Tender' : 'Approve Pengadaan Langsung'}
            </Btn>
          )}
          {s.status === 'APPROVED' && (
            <Btn onClick={handleSubmitPayment} disabled={actioning}>{actioning ? 'Memproses...' : 'Buat Payment Request'}</Btn>
          )}
          {s.status === 'ORDERED' && (
            <Btn onClick={handleReceive} disabled={actioning}>{actioning ? 'Memproses...' : 'Terima Barang'}</Btn>
          )}
          {s.status === 'RECEIVED' && (
            <Btn onClick={handleComplete} disabled={actioning}>Selesaikan</Btn>
          )}
        </div>
      </Card>
    </div>
  );
}